import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MailCheck } from "lucide-react";
import { useStore } from "@/context/StoreContext";

export default function ProductCard({ product, index = 0 }) {
  const store = useStore();
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
    el.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
  };

  const cardDenomination = Number(product.catalogDenomination || (product.denominations?.length ? Math.min(...product.denominations) : 0));
  const cardPrice = Number(product.catalogPrice || product.salePrice || cardDenomination || 0);
  const isGlobal = product.region === "global";
  const now = Date.now();
  const offer = (store.banners || [])
    .filter((b) => b.enabled && (b.placement === "market" || b.placement === "both" || b.discount_type !== "none"))
    .filter((b) => !b.start_at || !Number.isFinite(Date.parse(b.start_at)) || Date.parse(b.start_at) <= now)
    .filter((b) => !b.end_at || !Number.isFinite(Date.parse(b.end_at)) || Date.parse(b.end_at) >= now)
    .filter((b) => !b.market || b.market === "all" || b.market === store.market)
    .filter((b) => !b.category_slug || b.category_slug === product.category)
    .filter((b) => !b.target_products?.length || b.target_products.includes(product.slug))
    .filter((b) => b.discount_type !== "fixed" || (b.currency && b.currency !== "all" && String(b.currency).toUpperCase() === String(store.currency || "USD").toUpperCase()))
    .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0))[0];

  const originLabel = isGlobal
    ? `🌐 Global · ${product.redeemableCountries?.length || 0} countries`
    : `${store.countryFlag(product.country)} ${store.countryName(product.country) || product.country}`;

  const stock = product.stock;
  const stockLabel = stock === null || stock === undefined || stock <= 0
    ? "Sold out"
    : stock <= 0
      ? "Sold out"
      : stock === 1
        ? "Only 1 left"
        : stock <= 3
          ? `Only ${stock} left`
          : `${stock} available`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay: (index % 3) * 0.1, ease: [0.21, 0.65, 0.16, 1] }}
    >
      <Link to={`/product/${product.slug}`} data-testid={`product-card-${product.slug}`} className="group block">
        <div
          ref={ref}
          onMouseMove={onMove}
          className="card-spotlight neon-surface overflow-hidden rounded-2xl transition-[transform,border-color,box-shadow] duration-300 group-hover:-translate-y-1.5 group-hover:border-primary/30 group-hover:shadow-[0_20px_60px_rgba(0,0,0,0.65)]"
        >
          <div className="relative aspect-[1.586/1] overflow-hidden">
            <img
              src={product.image}
              alt={`${product.name} artwork`}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            />

            <div className="absolute bottom-3 left-3 rounded-xl border border-primary/40 bg-black/75 px-3 py-2 shadow-[0_0_28px_rgba(250,220,0,0.18)] backdrop-blur-md">
              <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-primary/80">Gift card value</p>
              <p className="mt-0.5 font-mono text-lg font-bold text-white">{store.fmtRaw(cardDenomination, product.currency)}</p>
            </div>
            {offer?.offer_label && (
              <span className="absolute left-3 top-3 z-10 rounded-full border border-primary/40 bg-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-foreground shadow-lg">
                {offer.offer_label}
              </span>
            )}
            <span className="absolute right-3 top-3 rounded-full border border-white/15 bg-black/45 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-white/85 backdrop-blur-md">
              {originLabel}
            </span>
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/95 via-black/65 to-transparent px-4 pb-3 pt-10">
              <span className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${stock === 0 ? "text-red-300" : stock > 0 && stock <= 3 ? "text-amber-300" : "text-emerald-300"}`}>
                ● {stockLabel}
              </span>
              <span className="text-[10px] text-white/75">{product.deliveryTime || "Digital delivery"}</span>
            </div>
          </div>

          <div className="mt-4 flex items-start justify-between gap-3 px-0.5 pb-1">
            <div>
              <p className="overline-label">
                {product.brand} · {store.categoryName(product.category)}
              </p>
              <h3 className="mt-1 font-display text-base font-semibold tracking-tight text-foreground transition-colors duration-200 group-hover:text-primary">
                {product.name}
              </h3>
              <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                <MailCheck className="h-3 w-3 text-primary/70" /> Digital delivery
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground">{product.currency} · {product.catalogDenomination ? "Single denomination" : (product.denominations?.length || 0) + " values"}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-muted-foreground">Sell price</p>
              <p className="font-mono text-sm font-semibold text-foreground">{store.fmtRaw(cardPrice, product.currency)}</p>
              {cardPrice !== cardDenomination && <p className="text-[10px] text-muted-foreground/70">Value {store.fmtRaw(cardDenomination, product.currency)}</p>}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
